/* eslint-disable no-param-reassign */
import React, { useEffect, MutableRefObject, useCallback } from 'react'

import { WorkWithUs } from '@/service/api/culturePage/types'
import { useScreen } from '@/store/screen'
import { getRandomElement } from '@/utils/randomElement'
import { shuffle } from '@/utils/shuffle'

export const useWorkWithUs = (data: WorkWithUs) => {
  const { profileImages } = data

  const { windowSize } = useScreen()

  const getResponsiveness = useCallback(() => {
    const responsivenessByScreen = {
      1440: {
        title: 'h3' as const,
        button: 'b1' as const,
        images: { numberOfImagesShown: 8, width: '213px', height: '236px' },
      },
      922: {
        title: 'h3' as const,
        button: 'b2' as const,
        images: { numberOfImagesShown: 4, width: '150px', height: '166px' },
      },
      742: {
        title: 'h3' as const,
        button: 'b3' as const,
        images: { numberOfImagesShown: 4, width: '97px', height: '106px' },
      },
      600: {
        title: 'h5' as const,
        button: 'b4' as const,
        images: { numberOfImagesShown: 4, width: '97px', height: '106px' },
      },
    }

    if (!windowSize?.width) return responsivenessByScreen[1440]
    if (windowSize?.width <= 600) return responsivenessByScreen[600]
    if (windowSize?.width <= 742) return responsivenessByScreen[742]
    if (windowSize?.width <= 922) return responsivenessByScreen[922]

    return responsivenessByScreen[1440]
  }, [windowSize?.width])

  const imageData = Array.from(
    {
      length: getResponsiveness().images.numberOfImagesShown,
    },
    () => React.createRef() as MutableRefObject<HTMLDivElement>,
  )

  useEffect(() => {
    const loadRefs = () => {
      const images = [...profileImages]
      shuffle(images)

      imageData.forEach((image) => {
        const { width, height } = getResponsiveness().images
        const randomImage = getRandomElement(images)
        images.splice(images.indexOf(randomImage), 1)

        image.current.style.width = width
        image.current.style.height = height
        image.current.style.background = `url(${randomImage.url})`
        image.current.style.backgroundSize = 'cover'
        image.current.style.backgroundRepeat = 'no-repeat'
        image.current.style.backgroundPosition = 'center'
      })
    }

    const changeImages = () => {
      const chosenImageToChange = getRandomElement(imageData)
      const chosenImageIndex = imageData.indexOf(chosenImageToChange)

      const unusedImages = profileImages.filter(
        (image) =>
          !imageData.some((imageInScreen) => {
            const { url } = image
            const { background } = imageInScreen.current.style
            return background.includes(url)
          }),
      )
      const randomUnusedImage = getRandomElement(unusedImages)

      if (
        !randomUnusedImage ||
        !randomUnusedImage.url ||
        chosenImageIndex === -1
      )
        return

      imageData[chosenImageIndex].current.animate(
        [
          {
            opacity: 1,
          },
          {
            opacity: 0,
          },
          {
            opacity: 0,
            background: `url(${randomUnusedImage.url})`,
            backgroundSize: 'cover',
          },
          {
            opacity: 0,
            background: `url(${randomUnusedImage.url})`,
            backgroundSize: 'cover',
          },
          {
            opacity: 1,
            background: `url(${randomUnusedImage.url})`,
            backgroundSize: 'cover',
          },
        ],
        { duration: 1000, fill: 'forwards' },
      )
      imageData[
        chosenImageIndex
      ].current.style.background = `url(${randomUnusedImage.url})`
    }

    loadRefs()
    const interval = setInterval(changeImages, 2000)

    return () => {
      clearInterval(interval)
    }
  }, [getResponsiveness, imageData, profileImages])

  return {
    imageData,
    responsiveness: getResponsiveness(),
  }
}
